import React, { useState } from "react";
import styled, { css } from "styled-components";
import CustomerUser from "types/CustomerUser";
import Link from "next/link";
import PersonPlus from "components/icons/personplus";
import Pet from "components/icons/pet";
import PropTypes from "prop-types";
import VendorUser from "types/VendorUser";
import ActionItem from "../ActionItem";
import BusinessInfoIcon from "../icons/storefront";
import CloseIcon from "../icons/close";
import NavigationDropdown from "../NavigationDropdown";
import GlobalFooter from "../GlobalFooter";
import GlobalSide from "../GlobalSide";
import Logo from "../icons/logo-with-tm";
import LogoMobile from "../icons/logomobile";
import MenuIcon from "../icons/menu";
import useCustomerUser from "../../hooks/use-customer-user";
import useOutsideClickHider from "../helpers/OutsideClickHider";
import useVendorUser from "../../hooks/use-vendor-user";

const IconWrapper = styled.div`
  margin: 20px 25px 20px 25px;
  cursor: pointer;
`;

const MenuWrapper = styled.div`
  height: 100%;
  border: 1px transparent;

  ${({ theme }) => theme.mq.lg`
    display: none;
  `}
`;

const Menu = React.forwardRef((props, ref) => {
  const { setHideSide, hideSide } = props;

  return (
    <MenuWrapper>
      <IconWrapper
        ref={ref}
        onClick={() => {
          setHideSide(!hideSide);
        }}
      >
        {hideSide ? <MenuIcon color="#27272F" /> : <CloseIcon />}
      </IconWrapper>
    </MenuWrapper>
  );
});

Menu.propTypes = {
  hideSide: PropTypes.bool,
  setHideSide: PropTypes.func,
};

Menu.defaultProps = {
  hideSide: true,
  setHideSide: {},
};

const actionItems = [
  {
    id: 1,
    text: "List your Business",
    redirectUrl: "/provider/auth",
    Icon: BusinessInfoIcon,
  },
];

const loginItems = [
  {
    id: 1,
    text: "LOGIN",
    Icon: Pet,
    isLogin: true,
  },
  {
    id: 2,
    text: "SIGN UP",
    redirectUrl: "/customer/auth",
    Icon: PersonPlus,
  },
];

const dropdownItems = [
  {
    id: 1,
    text: "AS PET PARENT",
    redirectUrl: "/customer/auth",
    Icon: Pet,
  },
  {
    id: 2,
    text: "AS BUSINESS OWNER",
    redirectUrl: "/provider/auth",
    Icon: BusinessInfoIcon,
  },
];

const genLogoutItem = (usertype) => {
  return {
    text: "Logout",
    noBottomMargin: true,
    redirectUrl: `/${usertype}/auth`,
    isLogout: true,
    usertype,
  };
};

const GridWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-template-areas:
    "header header"
    "main main"
    "footer footer";

  min-height: 100vh;

  ${({ theme }) => theme.mq.lg`
  grid-template-columns: 320px 1fr;
  grid-template-rows: 70px auto fit-content(250px);
  grid-template-areas:
    "header header"
    "main main"
    "footer footer";
  `}

  ${({ theme }) => theme.mq.ltSm`
    grid-template-rows: 4.375rem auto fit-content(18.75rem);
  `}
`;

const Header = styled.nav`
  background-color: ${({ theme }) => theme.themeColors.white};
  box-shadow: ${({ theme }) => theme.settings.shadows.boxShadow};
  display: flex;
  grid-area: header;
  height: 70px;
  justify-content: space-between;
  width: 100%;
  z-index: 5;
`;

const HeaderButtons = styled.div`
  margin: auto 0;
  display: flex;
`;

const Main = styled.div`
  grid-area: main;
  width: 100%;
`;

const HeaderActionItemWrapper = styled.div`
  display: none;

  ${({ theme }) => theme.mq.lg`
    display: flex;
    margin: 0.8125rem 1.5rem 0.9375rem 1.875rem;
    border: 1px solid ${theme.themeColors.primary};
    border-radius: 48px;
    min-width: 11.875rem;
    height: 2.625rem;

    a {
      font-size: 16px;
      height: 100%;
      width: 100%;
      border-radius: 48px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: ${theme.themeColors.primary};
      z-index: 2;

      &:hover {
        color: ${theme.themeColors.lightGreen1};
        background-color: ${theme.themeColors.primary};
      }
    }
  `}
`;

const HeaderLoginItemWrapper = styled.div`
  margin: auto 15px;
  display: inline-block;
  ${({ theme }) => theme.mq.ltLg`
    display: none;
  `}
`;

const LogoWrapperLg = styled.div`
  margin: auto 0 auto 30px;
  display: block;
  ${({ theme }) => theme.mq.ltLg`
    display: none;
    margin: auto 30px;
  `};
`;

const LogoWrapperMd = styled.div`
  margin: auto 0 auto 30px;
  display: none;
  ${({ theme }) => theme.mq.ltLg`
    display: block;
    margin: auto 25px;
  `};
`;

const LoginButtonsWrapper = styled.div`
  display: none;
    ${({ theme }) => theme.mq.lg`
    display: flex;
    align-items: center;
    margin: auto 15px auto 25px;
    height: 70px;
    a {
      cursor: pointer;
      font-size: ${theme.fontSize(1)};
      padding: 12px 9px 12px 9px;
      transition: ${theme.opacityTransition};
    `}
  }
`;

const Overlay = styled.div`
  display: none;
  opacity: 0;
  overflow: hidden;

  ${({ isSideNavVisible }) =>
    !isSideNavVisible &&
    css`
      background-color: #5a5b64;
      display: block;
      height: 100%;
      left: 0;
      opacity: 0.5;
      position: fixed;
      top: 0;
      width: 100%;
      z-index: 2;
    `}
`;

const GenericNavigation = (props) => {
  const [hideSide, setHideSide] = useState(true);
  const sideNavRef = React.useRef(null);
  const IconEl = React.useRef(null);
  useOutsideClickHider([sideNavRef, IconEl], setHideSide);
  const { children } = props;
  const vendorUser = useVendorUser();
  const customerUser = useCustomerUser();

  return (
    <GridWrapper hideSide={hideSide}>
      <Header>
        <LogoWrapperLg>
          <Link href="/">
            <a>
              <Logo color="#FF7817" />
            </a>
          </Link>
        </LogoWrapperLg>
        <LogoWrapperMd>
          <Link href="/">
            <a>
              <LogoMobile color="#FF7817" />
            </a>
          </Link>
        </LogoWrapperMd>
        <HeaderButtons>
          {!(vendorUser?.id || customerUser?.id) ? (
            <>
              <LoginButtonsWrapper>
                <NavigationDropdown listItems={dropdownItems} right="17rem" />
              </LoginButtonsWrapper>
              <HeaderActionItemWrapper>
                <Link href="/provider/auth">List your business</Link>
              </HeaderActionItemWrapper>
            </>
          ) : (
            <LoginButtonsWrapper>
              <HeaderLoginItemWrapper>
                <ActionItem
                  defaultColor="#FF7817"
                  fontSize="14px"
                  {...genLogoutItem(vendorUser?.id ? "provider" : "customer")}
                />
              </HeaderLoginItemWrapper>
            </LoginButtonsWrapper>
          )}
          <Menu ref={IconEl} setHideSide={setHideSide} hideSide={hideSide} />
        </HeaderButtons>
      </Header>
      <Main initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
        <>
          <Overlay isSideNavVisible={hideSide} />
          {children}
        </>
      </Main>
      <GlobalSide
        hideLg
        sideBarItems={actionItems}
        loginItems={loginItems}
        ref={sideNavRef}
        hideSide={hideSide}
        {...genLogoutItem(vendorUser?.id ? "provider" : "customer")}
      />
      <GlobalFooter />
    </GridWrapper>
  );
};

GenericNavigation.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
  vendorUser: VendorUser,
  customerUser: CustomerUser,
};

GenericNavigation.defaultProps = {
  vendorUser: null,
  customerUser: null,
};

export default GenericNavigation;
